import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import {
  mapBackendConditionsToFrontend,
  mapFrontendConditionsToBackend,
} from 'utils/wantListDefaultsMapper';
import {
  mapBackendToFrontend as mapBackendLanguagesToFrontend,
  mapFrontendToBackend as mapFrontendLanguagesToBackend,
} from 'constants/languages';

/**
 * Backend response format for want list defaults
 */
interface WantListDefaultsBackend {
  defaultAcceptFoil: boolean;
  defaultAcceptNonFoil: boolean;
  defaultLanguages: string[]; // lowercase: ['en', 'es', 'fr', ...]
  defaultConditions: string[]; // uppercase: ['NEAR_MINT', 'SLIGHTLY_PLAYED', ...]
}

/**
 * Frontend format for want list defaults (used by UI components)
 */
export interface WantListDefaults {
  defaultAcceptFoil: boolean;
  defaultAcceptNonFoil: boolean;
  defaultLanguages: string[]; // uppercase: ['EN', 'ES', 'FR', ...]
  defaultConditions: string[]; // codes: ['NM', 'LP', 'MP', ...]
}

/**
 * Convert backend response to frontend format
 */
function transformBackendToFrontend(backend: WantListDefaultsBackend): WantListDefaults {
  return {
    defaultAcceptFoil: backend.defaultAcceptFoil,
    defaultAcceptNonFoil: backend.defaultAcceptNonFoil,
    defaultLanguages: mapBackendLanguagesToFrontend(backend.defaultLanguages),
    defaultConditions: mapBackendConditionsToFrontend(backend.defaultConditions),
  };
}

/**
 * Convert frontend format to backend request format
 */
function transformFrontendToBackend(frontend: Partial<WantListDefaults>): Partial<WantListDefaultsBackend> {
  const backend: Partial<WantListDefaultsBackend> = {};

  if (frontend.defaultAcceptFoil !== undefined) {
    backend.defaultAcceptFoil = frontend.defaultAcceptFoil;
  }
  if (frontend.defaultAcceptNonFoil !== undefined) {
    backend.defaultAcceptNonFoil = frontend.defaultAcceptNonFoil;
  }
  if (frontend.defaultLanguages !== undefined) {
    backend.defaultLanguages = mapFrontendLanguagesToBackend(frontend.defaultLanguages);
  }
  if (frontend.defaultConditions !== undefined) {
    backend.defaultConditions = mapFrontendConditionsToBackend(frontend.defaultConditions);
  }

  return backend;
}

/**
 * Have list defaults (used for received cards behavior)
 */
export interface HaveListDefaults {
  receivedCardsForTrade: boolean;
}

export const settingsApi = createApi({
  reducerPath: 'settingsApi',
  baseQuery: baseQuery,
  tagTypes: ['WantListDefaults', 'HaveListDefaults'],
  endpoints: (builder) => ({
    getWantListDefaults: builder.query<WantListDefaults, void>({
      query: () => ({
        url: '/settings/want-list-defaults',
        method: 'GET',
        requiresAuth: true,
      }),
      transformResponse: (response: WantListDefaultsBackend) => {
        return transformBackendToFrontend(response);
      },
      providesTags: ['WantListDefaults'],
    }),

    updateWantListDefaults: builder.mutation<
      { success: boolean; settings: WantListDefaults },
      Partial<WantListDefaults>
    >({
      query: (body) => ({
        url: '/settings/want-list-defaults',
        method: 'PUT',
        body: transformFrontendToBackend(body),
        requiresAuth: true,
      }),
      transformResponse: (response: { success: boolean; settings: WantListDefaultsBackend }) => {
        return {
          success: response.success,
          settings: transformBackendToFrontend(response.settings),
        };
      },
      invalidatesTags: ['WantListDefaults'],
    }),

    getHaveListDefaults: builder.query<HaveListDefaults, void>({
      query: () => ({
        url: '/settings/have-list-defaults',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['HaveListDefaults'],
    }),

    updateHaveListDefaults: builder.mutation<
      { success: boolean; settings: HaveListDefaults },
      Partial<HaveListDefaults>
    >({
      query: (body) => ({
        url: '/settings/have-list-defaults',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['HaveListDefaults'],
    }),
  }),
});

export const {
  useGetWantListDefaultsQuery,
  useUpdateWantListDefaultsMutation,
  useGetHaveListDefaultsQuery,
  useUpdateHaveListDefaultsMutation,
} = settingsApi;
